import {
  Badge,
  Box,
  Button,
  CountryFlag,
  FlexBox,
  IconButton,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { produce } from 'immer';
import { getCountryCodeFromPhoneNumber } from 'lib-shared-phone-numbers';
import { useMemo, useRef } from 'react';
import { useDebounce } from 'react-use';

import { numbersApi } from 'apis/numbers';
import { appActions } from 'app/app.actions';
import { getCountryNameFromCountryCode } from 'utils/countries';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { smsFunctionalityStateText } from '../utils/smsFunctionality';

import styles from './smsTableRow.module.scss';
import { SmsTableRowProps } from './smsTableRow.types';

/** Represents a row. */
export const SmsTableRow = ({
  smsTableDataItem,
  onSmsTableDataItemChanged,
}: SmsTableRowProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const previousSmsFunctionality = useRef(
    smsTableDataItem.phoneNumber.sms_functionality,
  );

  const { countryCode, countryName } = useMemo(() => {
    const countryCode = getCountryCodeFromPhoneNumber(
      smsTableDataItem.phoneNumber.e164_format,
    );

    return {
      countryCode,
      countryName: countryCode && getCountryNameFromCountryCode(countryCode),
    };
  }, [smsTableDataItem.phoneNumber]);

  // Debounces the network request when the button gets pressed in quick
  // succession.
  useDebounce(
    () => {
      const { e164_format, sms_functionality } = smsTableDataItem.phoneNumber;

      if (sms_functionality !== previousSmsFunctionality.current) {
        dispatch(
          numbersApi.endpoints.updateNumber.initiate({
            phoneNumber: e164_format,
            data: { sms_functionality },
          }),
        )
          .unwrap()
          .then(() => {
            previousSmsFunctionality.current = sms_functionality;
          })
          .catch(() => {
            // Undo the table change as there was an error. This will
            // effectively flip the switch back to its previous value.
            onSmsTableDataItemChanged(
              produce(smsTableDataItem, (draft) => {
                draft.phoneNumber.sms_functionality =
                  previousSmsFunctionality.current;
              }),
            );
          });
      }
    },
    500,
    [smsTableDataItem.phoneNumber.sms_functionality],
  );

  /** Handles changing the queue of an sms phone number. */
  const handleSmsQueueChange = () => {
    dispatch(
      appActions.showModal({
        modal: { type: 'settings:editSmsQueue', arg: { smsTableDataItem } },
      }),
    );
  };

  /** Handles changing the sms functionality of an sms phone number. */
  const handleSmsFunctionalityChange = () => {
    onSmsTableDataItemChanged(
      produce(smsTableDataItem, (draft) => {
        draft.phoneNumber.sms_functionality =
          !smsTableDataItem.phoneNumber.sms_functionality;
      }),
    );
  };

  /**
   * Handles clicking the request for change of sms functionality for manual
   * carriers. (DIDWW)
   */
  const handleSmsFunctionalityStateChange = async () => {
    const { updatedSmsTableDataItem } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:changeSmsFunctionality',
          arg: { smsTableDataItem },
        },
      }),
    );

    onSmsTableDataItemChanged(updatedSmsTableDataItem);
  };

  const {
    phoneNumber: {
      carrier,
      e164_format,
      name,
      sms_functionality,
      sms_functionality_state,
    },
    queue,
  } = smsTableDataItem;

  return (
    <Table.Tr>
      <Table.Td>
        <FlexBox gap={sizes.spacer['300']}>
          {countryCode && (
            <Box className={styles.countryFlagContainer}>
              <CountryFlag countryCode={countryCode} size={20} />
            </Box>
          )}
          <FlexBox flexDirection="column">
            <Text.Paragraph variant="2">{e164_format}</Text.Paragraph>
            {countryName && (
              <Text.Paragraph variant="4" subdued>
                {countryName}
              </Text.Paragraph>
            )}
          </FlexBox>
        </FlexBox>
      </Table.Td>
      <Table.Td>{name}</Table.Td>
      <Table.Td>
        {queue ? (
          <FlexBox gap="250" alignItems="center">
            <Text.Paragraph>{queue.name}</Text.Paragraph>

            {queue.is_archived && (
              <Badge variant="error" size="small">
                Archived
              </Badge>
            )}
          </FlexBox>
        ) : (
          <Text.Paragraph as="i">No queue</Text.Paragraph>
        )}
      </Table.Td>
      <Table.Td>
        <FlexBox
          alignItems="center"
          justifyContent="space-between"
          gap={sizes.spacer['300']}
        >
          <Switch
            name={name ?? ''}
            value={name ?? ''}
            checked={sms_functionality}
            onChange={handleSmsFunctionalityChange}
            disabled={carrier === 'didww'}
          />
          {carrier === 'didww' && sms_functionality_state && (
            <Button
              className={styles.button}
              variant="secondary"
              onClick={handleSmsFunctionalityStateChange}
              disabled={['pending_activation', 'pending_deactivation'].includes(
                sms_functionality_state,
              )}
            >
              {smsFunctionalityStateText[sms_functionality_state]}
            </Button>
          )}

          <Tooltip content="Edit" dark>
            <IconButton
              variant="tertiary"
              icon="pen"
              onClick={handleSmsQueueChange}
            />
          </Tooltip>
        </FlexBox>
      </Table.Td>
    </Table.Tr>
  );
};
